import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  AutocompleteResponse,
  BuiltinControlAutocompleteRequest,
  ControlState,
  EntityFeatureGetResponse,
  EntityFeaturePatchConflictResponse,
  EntityFeaturePatchRequest,
} from '../../../api/model/models';
import { CfContextService } from '../../../utils';
import { FastDeepEqual, Rfdc } from '../../../vendors';

@Injectable()
export class CplaceControlService {
  public static readonly ENTITY_FEATURE_ENDPOINT = 'cplace-api/cf.cplace.platform/entity/feature';
  public static readonly SEARCH_BUILTIN_CONTROL_AUTOCOMPLETE_ENDPOINT = 'cplace-api/cf.cplace.platform/search/builtin-control-autocomplete';

  public static readonly ATTR_DATA_COMPONENT_ID = 'data-component-id';
  public static readonly ATTR_DATA_ELEMENT_NAME = 'data-element-name';
  public static readonly CONTROL_CONTENT_ELEMENT_NAME = 'control-content';
  public static readonly CANCEL_BUTTON_ELEMENT_NAME = 'cancel-button';

  /**
   * Used to perform a deep equal operation.
   */
  public readonly deepEqual = FastDeepEqual.default();

  private readonly rfdc = Rfdc.default();

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  /**
   * Tells whether any control is currently in edit mode.
   * @private
   */
  private _controlInEditMode = false;

  /**
   * The component ID of the control containing the last clicked element.
   * @private
   */
  private _containingControlComponentId: string | null = null;

  constructor(private httpClient: HttpClient, private cfContextService: CfContextService) {
    this.createDocumentClickListener();
  }

  /**
   * Returns whether the provided {@link componentId} is equal to the {@link _containingControlComponentId}.
   *
   * @param componentId The component ID to check against {@link _containingControlComponentId}.
   */
  public lastClickedElementContainedWithin(componentId: string): boolean {
    return componentId === this._containingControlComponentId;
  }

  /**
   * Resets the {@link _containingControlComponentId}.
   */
  public resetContainingControlComponentId() {
    this._containingControlComponentId = null;
  }

  /**
   * Sets whether a control is in edit mode. This enables the document click listener created in {@link
   * createDocumentClickListener}.
   *
   * @param controlInEditMode Whether a control is in edit mode.
   */
  public setControlInEditMode(controlInEditMode: boolean) {
    this._controlInEditMode = controlInEditMode;
  }

  /**
   * Used to perform a deep clone operation.
   * @param value The value to clone.
   */
  public deepClone<T>(value: T | undefined): T | undefined {
    const clonedValue = this.rfdc(value);
    return clonedValue as T | undefined;
  }

  /**
   * Submits a control change request and returns updated control states or a conflict response when validation fails.
   * @param entityFeaturePatchRequest Entity feature patch request.
   */
  public submit(entityFeaturePatchRequest: EntityFeaturePatchRequest): Observable<ControlState[] | EntityFeaturePatchConflictResponse> {
    return this.httpClient
      .patch<ControlState[] | EntityFeaturePatchConflictResponse>(
        this.cfContextService.context + CplaceControlService.ENTITY_FEATURE_ENDPOINT,
        entityFeaturePatchRequest,
        this.httpOptions
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Conflict) {
            return of(error.error);
          }

          return throwError(error.error);
        })
      );
  }

  /**
   * Gets the specified control states from the features of an entity.
   *
   * @param entityUid entity uid.
   * @param featureNames Array of feature names.
   */
  public get(entityUid: string, featureNames: Array<string>): Observable<EntityFeatureGetResponse> {
    return this.httpClient.get<EntityFeatureGetResponse>(this.cfContextService.context + CplaceControlService.ENTITY_FEATURE_ENDPOINT, {
      params: new HttpParams({
        fromObject: { featureNames: featureNames, entityUid: entityUid },
      }),
    });
  }

  /**
   * Gets the autocomplete menu items to be displayed.
   *
   * @param request An {@link BuiltinControlAutocompleteRequest}.
   * @param featureName Feature name. Only used for mocking purposes.
   */
  public searchBuiltinControlAutocompleteGet(
    request: BuiltinControlAutocompleteRequest,
    featureName: string
  ): Observable<AutocompleteResponse> {
    const options = { params: new HttpParams().set('request', JSON.stringify(request)) };
    return this.httpClient.get<AutocompleteResponse>(
      this.cfContextService.context + CplaceControlService.SEARCH_BUILTIN_CONTROL_AUTOCOMPLETE_ENDPOINT,
      options
    );
  }

  /**
   * Creates a document click listener used for checking if the clicked element is contained within a control.
   * @private
   */
  private createDocumentClickListener() {
    document.addEventListener('click', (event) => {
      if (this._controlInEditMode && event.target instanceof HTMLElement) {
        this._containingControlComponentId = this.getContainingControlComponentId(event.target);
      }
    });
  }

  /**
   * Recursively checks if the parent element is contained within a control. If so, returns the component ID.
   * @param element The {@link HTMLElement} to check.
   * @private
   */
  private getContainingControlComponentId(element: HTMLElement | null): string | null {
    if (element === null) {
      return null;
    }

    const componentIdAttr = element.attributes.getNamedItem(CplaceControlService.ATTR_DATA_COMPONENT_ID);
    const elementNameAttr = element.attributes.getNamedItem(CplaceControlService.ATTR_DATA_ELEMENT_NAME);

    if (
      componentIdAttr !== null &&
      elementNameAttr !== null &&
      elementNameAttr.value === CplaceControlService.CONTROL_CONTENT_ELEMENT_NAME
    ) {
      return componentIdAttr.value;
    }

    return this.getContainingControlComponentId(element.parentElement);
  }
}
